import { useMemo } from 'react';
import { MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { MultiSelectFilter, type FilterGroup } from '@/shared/ui/filters/multi-select-filter';
import type { LocationGroup } from '@/entities/dashboard';

interface LocationsFilterProps {
  locationGroups: LocationGroup[] | undefined;
  selectedLocations: string[];
  onLocationsChange: (locations: string[]) => void;
  isLoading?: boolean;
}

export function LocationsFilter({
  locationGroups,
  selectedLocations,
  onLocationsChange,
  isLoading = false
}: LocationsFilterProps) {
  const { t } = useTranslation();

  const filterGroups: FilterGroup[] = useMemo(() => {
    if (!locationGroups) return [];

    return locationGroups.map((group) => ({
      group: group.group,
      groupId: group.groupId,
      items: group.locations.map((location) => ({
        id: location.id,
        name: location.name
      }))
    }));
  }, [locationGroups]);

  return (
    <MultiSelectFilter
      label={t('dashboard.locations.label', 'Locations')}
      placeholder={t('dashboard.locations.placeholder', 'Select locations...')}
      searchPlaceholder={t('dashboard.locations.search', 'Search locations...')}
      emptyText={t('dashboard.locations.empty', 'No locations found')}
      icon={MapPin}
      groups={filterGroups}
      selectedItems={selectedLocations}
      onItemsChange={onLocationsChange}
      isLoading={isLoading}
      showSelectAll={true}
      maxDisplayItems={3}
    />
  );
}
